<script setup lang="ts">
import { ref } from 'vue'
import { Info, Settings2 } from 'lucide-vue-next'
import AppModal from '@/components/shared/AppModal.vue'
import AboutConfig from './AboutConfig.vue'
import DeviceInfo from '@/environments/DeviceInfo.vue'

const props = defineProps<{ isOpen: boolean }>()
const emit = defineEmits<{
  (e: 'update:is-open', value: boolean): void
}>()

const activeTab = ref<'info' | 'config'>('info')

function close() {
  emit('update:is-open', false)
}
</script>

<template>
  <AppModal
    :visible="props.isOpen"
    @update:visible="emit('update:is-open', $event)"
    @close="close"
    title="About"
    description="Application information and settings"
    has-sidebar
    max-width="max-w-6xl"
    height="h-[750px]"
    :z-index="2000"
  >
    <template #sidebar>
      <nav class="grid grid-cols-2 md:flex md:flex-col gap-1 max-md:pb-2">
        <button 
          @click="activeTab = 'info'"
          :class="[
            'flex-1 flex items-center justify-center md:justify-start gap-3 px-4 py-2 md:py-3 rounded-xl transition-all font-medium text-sm whitespace-nowrap',
            activeTab === 'info' 
              ? 'bg-primary text-white dark:text-gray-950 shadow-lg shadow-primary/20 font-bold' 
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5'
          ]"
        >
          <Info class="w-4 h-4" />
          Information
        </button>
        
        <button 
          @click="activeTab = 'config'"
          :class="[
            'flex-1 flex items-center justify-center md:justify-start gap-3 px-4 py-2 md:py-3 rounded-xl transition-all font-medium text-sm whitespace-nowrap',
            activeTab === 'config' 
              ? 'bg-primary text-white dark:text-gray-950 shadow-lg shadow-primary/20 font-bold' 
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5'
          ]"
        >
          <Settings2 class="w-4 h-4" />
          Configuration
        </button>
      </nav>
    </template>

    <Transition name="fade-slide" mode="out-in">
      <div v-if="activeTab === 'info'" key="info" class="flex flex-col flex-1 min-h-0 overflow-x-hidden">
        <DeviceInfo />
      </div>

      <div v-else-if="activeTab === 'config'" key="config" class="flex flex-col flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        <AboutConfig />
      </div>
    </Transition>
  </AppModal>
</template>


<style scoped>
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateX(30px);
}
.fade-slide-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}
</style>